import type { TMDBCastCredit, TMDBMovieDetails } from "@/lib/tmdb/schemas";
import type {
  CastMember,
  Movie,
  MovieDetails,
  MovieFilters,
  MovieSortOptions,
  PaginatedMovies,
} from "@/types/movie";
import { getImageUrl, TMDB_CONFIG } from "@/lib/tmdb/config";
import { generateSlug } from "@/lib/utils/slug";

import {
  compareAwards,
  parseAwards,
  parseBoxOffice,
  parseMetacritic,
  parsePercentage,
  parseScore,
} from "./ratings";

/**
 * Pure movie helpers shared by server code and the browse page client:
 * TMDB -> app transforms, filtering, sorting and release-date logic.
 */

/** Parse a TMDB "YYYY-MM-DD" date (UTC) and its year. */
export function parseReleaseDate(dateString: string | null | undefined): {
  date: Date | null;
  year: number | null;
} {
  if (!dateString) return { date: null, year: null };

  const date = new Date(`${dateString.slice(0, 10)}T00:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return { date: null, year: null };
  }

  return { date, year: date.getUTCFullYear() };
}

/** Has the movie been released (as of `now`)? Movies without a date have not. */
export function isReleased(
  movie: Pick<Movie, "releaseDate">,
  now: Date = new Date(),
): boolean {
  return (
    !!movie.releaseDate &&
    new Date(movie.releaseDate).getTime() <= now.getTime()
  );
}

/** Does the movie have at least one meaningful rating to show and sort by? */
export function hasMeaningfulRating(
  movie: Pick<Movie, "externalRatings" | "rating" | "voteCount">,
  minTmdbVotes = 10,
): boolean {
  return Boolean(
    parseScore(movie.externalRatings?.imdb) !== null ||
    parsePercentage(movie.externalRatings?.rottenTomatoesCritic) !== null ||
    (movie.rating > 0 && movie.voteCount > minTmdbVotes),
  );
}

const roundRating = (value: number) => Math.round(value * 10) / 10;

/** Fallback slug when no canonical slug is known (see slug-registry.ts). */
function fallbackSlug(id: number, title: string): string {
  return generateSlug(title, { foldAccents: true }) || `movie_${id}`;
}

/**
 * Transform a TMDB credit to the application Movie type.
 * Pass the canonical `slug` from the slug registry.
 */
export function transformTMDBMovie(
  tmdbMovie: TMDBCastCredit,
  genreMap: Map<number, string>,
  options: { slug?: string } = {},
): Movie {
  const { date, year } = parseReleaseDate(tmdbMovie.release_date);

  return {
    id: tmdbMovie.id,
    slug: options.slug ?? fallbackSlug(tmdbMovie.id, tmdbMovie.title),
    title: tmdbMovie.title,
    originalTitle: tmdbMovie.original_title,
    character: tmdbMovie.character || undefined,
    posterUrl: getImageUrl(tmdbMovie.poster_path, "poster", "medium"),
    backdropUrl: getImageUrl(tmdbMovie.backdrop_path, "backdrop", "large"),
    overview: tmdbMovie.overview,
    releaseDate: date,
    releaseYear: year,
    rating: roundRating(tmdbMovie.vote_average),
    voteCount: tmdbMovie.vote_count,
    genres: tmdbMovie.genre_ids
      .map((id) => genreMap.get(id))
      .filter((name): name is string => !!name),
    popularity: tmdbMovie.popularity,
  };
}

/** Top-billed cast with Nicolas Cage first. */
function topCast(details: TMDBMovieDetails, limit = 10): CastMember[] {
  const cast = [...(details.credits?.cast ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const cageIndex = cast.findIndex(
    (member) => member.id === TMDB_CONFIG.nicolasCageId,
  );

  if (cageIndex > 0) cast.unshift(...cast.splice(cageIndex, 1));

  return cast.slice(0, limit).map((member) => ({
    id: member.id,
    name: member.name,
    character: member.character,
    profileUrl: getImageUrl(member.profile_path, "profile", "medium"),
  }));
}

/**
 * Transform TMDB movie details to the application MovieDetails type.
 * Pass the canonical `slug` from the slug registry.
 */
export function transformTMDBMovieDetails(
  tmdbMovie: TMDBMovieDetails,
  _genreMap?: Map<number, string>,
  options: { slug?: string } = {},
): MovieDetails {
  const { date, year } = parseReleaseDate(tmdbMovie.release_date);
  const cage = tmdbMovie.credits?.cast.find(
    (member) => member.id === TMDB_CONFIG.nicolasCageId,
  );

  return {
    id: tmdbMovie.id,
    slug: options.slug ?? fallbackSlug(tmdbMovie.id, tmdbMovie.title),
    title: tmdbMovie.title,
    originalTitle: tmdbMovie.original_title,
    character: cage?.character || undefined,
    posterUrl: getImageUrl(tmdbMovie.poster_path, "poster", "medium"),
    backdropUrl: getImageUrl(tmdbMovie.backdrop_path, "backdrop", "large"),
    overview: tmdbMovie.overview,
    releaseDate: date,
    releaseYear: year,
    rating: roundRating(tmdbMovie.vote_average),
    voteCount: tmdbMovie.vote_count,
    genres: tmdbMovie.genres.map((genre) => genre.name),
    popularity: tmdbMovie.popularity,
    runtime: tmdbMovie.runtime,
    cast: topCast(tmdbMovie),
    imdbId: tmdbMovie.external_ids?.imdb_id ?? null,
    tagline: tmdbMovie.tagline,
    status: tmdbMovie.status,
  };
}

/** Drop detail-only fields (cast, tagline, status...) to keep lists lean. */
export function toListMovie(movie: Movie | MovieDetails): Movie {
  const {
    cast: _cast,
    tagline: _tagline,
    status: _status,
    rated: _rated,
    ...listMovie
  } = movie as MovieDetails;

  return listMovie;
}

/** Lowercase, accent-insensitive text for searching. */
export function normalizeSearchText(value: string): string {
  return value.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

function matchesTitle(movie: Movie, query: string): boolean {
  return [movie.title, movie.originalTitle ?? ""].some((title) =>
    normalizeSearchText(title).includes(query),
  );
}

type RangeFilter = {
  min?: number;
  max?: number;
  value: (movie: Movie) => number | null;
};

/**
 * Filter movies based on search query and filters
 */
export function filterMovies(movies: Movie[], filters: MovieFilters): Movie[] {
  const query = normalizeSearchText(filters.search ?? "");
  const allRanges: RangeFilter[] = [
    {
      min: filters.yearFrom,
      max: filters.yearTo,
      value: (movie) => movie.releaseYear,
    },
    {
      min: filters.ratingMin,
      max: filters.ratingMax,
      value: (movie) => movie.rating,
    },
    {
      min: filters.imdbMin,
      max: filters.imdbMax,
      value: (movie) => parseScore(movie.externalRatings?.imdb),
    },
    {
      min: filters.rottenTomatoesCriticMin,
      max: filters.rottenTomatoesCriticMax,
      value: (movie) =>
        parsePercentage(movie.externalRatings?.rottenTomatoesCritic),
    },
    {
      min: filters.rottenTomatoesAudienceMin,
      max: filters.rottenTomatoesAudienceMax,
      value: (movie) =>
        parsePercentage(movie.externalRatings?.rottenTomatoesAudience),
    },
    {
      min: filters.metacriticMin,
      max: filters.metacriticMax,
      value: (movie) => parseMetacritic(movie.externalRatings?.metacritic),
    },
    {
      min: filters.cageMdbMin,
      max: filters.cageMdbMax,
      value: (movie) => parseScore(movie.externalRatings?.cageMdb),
    },
  ];
  const ranges = allRanges.filter(
    (range) => range.min !== undefined || range.max !== undefined,
  );
  const genres = filters.genres ?? [];

  return movies.filter((movie) => {
    if (
      query &&
      !matchesTitle(movie, query) &&
      !normalizeSearchText(movie.overview).includes(query)
    ) {
      return false;
    }

    if (
      genres.length > 0 &&
      !movie.genres.some((genre) => genres.includes(genre))
    ) {
      return false;
    }

    return ranges.every(({ min, max, value }) => {
      const actual = value(movie);

      if (actual === null) return false;
      if (min !== undefined && actual < min) return false;
      if (max !== undefined && actual > max) return false;

      return true;
    });
  });
}

/** Sort key for each option; null sorts last regardless of direction. */
const SORT_VALUES: Record<
  MovieSortOptions["sortBy"],
  (movie: Movie) => number | string | null
> = {
  release_date: (movie) =>
    movie.releaseDate ? new Date(movie.releaseDate).getTime() : null,
  tmdb: (movie) => movie.rating,
  imdb: (movie) => parseScore(movie.externalRatings?.imdb),
  rotten_tomatoes_critic: (movie) =>
    parsePercentage(movie.externalRatings?.rottenTomatoesCritic),
  rotten_tomatoes_audience: (movie) =>
    parsePercentage(movie.externalRatings?.rottenTomatoesAudience),
  cage_mdb: (movie) => parseScore(movie.externalRatings?.cageMdb),
  box_office: (movie) => parseBoxOffice(movie.boxOffice),
  runtime: (movie) => movie.runtime ?? null,
  awards: () => null, // compared hierarchically below
  title: (movie) => movie.title,
  popularity: (movie) => movie.popularity,
};

function compareBy(
  a: Movie,
  b: Movie,
  sortBy: MovieSortOptions["sortBy"],
): number | null {
  if (sortBy === "awards") {
    const aAwards = parseAwards(a.awards);
    const bAwards = parseAwards(b.awards);

    if (!aAwards || !bAwards) {
      return !aAwards && !bAwards ? 0 : null;
    }

    return compareAwards(aAwards, bAwards);
  }

  const aValue = SORT_VALUES[sortBy](a);
  const bValue = SORT_VALUES[sortBy](b);

  if (aValue === null || bValue === null) {
    return aValue === null && bValue === null ? 0 : null;
  }

  if (typeof aValue === "string" && typeof bValue === "string") {
    return aValue.localeCompare(bValue);
  }

  return Number(aValue) - Number(bValue);
}

/**
 * Sort movies based on sort options. Movies without a value for the sort
 * key always go last. With a search query, title matches come first.
 */
export function sortMovies(
  movies: Movie[],
  options: MovieSortOptions,
  searchQuery?: string,
): Movie[] {
  const query = normalizeSearchText(searchQuery ?? "");

  return [...movies].sort((a, b) => {
    if (query) {
      const aInTitle = matchesTitle(a, query);
      const bInTitle = matchesTitle(b, query);

      if (aInTitle !== bInTitle) return aInTitle ? -1 : 1;
    }

    const comparison = compareBy(a, b, options.sortBy);

    if (comparison === null) {
      // Exactly one side is missing a value: it goes last.
      const aMissing =
        options.sortBy === "awards"
          ? !parseAwards(a.awards)
          : SORT_VALUES[options.sortBy](a) === null;

      return aMissing ? 1 : -1;
    }

    return options.direction === "desc" ? -comparison : comparison;
  });
}

/**
 * Paginate movies
 */
export function paginateMovies(
  movies: Movie[],
  page: number,
  perPage: number,
): PaginatedMovies {
  const total = movies.length;
  const totalPages = Math.ceil(total / perPage);
  const start = (page - 1) * perPage;
  const end = start + perPage;

  return {
    movies: movies.slice(start, end),
    total,
    page,
    perPage,
    totalPages,
  };
}

/**
 * Get year range from movies list
 */
export function getYearRange(movies: Movie[]): {
  min: number;
  max: number;
} {
  const years = movies
    .map((m) => m.releaseYear)
    .filter((year): year is number => year !== null);

  if (years.length === 0) {
    return { min: 1900, max: new Date().getUTCFullYear() };
  }

  return {
    min: Math.min(...years),
    max: Math.max(...years),
  };
}

/**
 * Get unique genres from movies list
 */
export function getUniqueGenres(movies: Movie[]): string[] {
  const genresSet = new Set<string>();

  movies.forEach((movie) => {
    movie.genres.forEach((genre) => genresSet.add(genre));
  });

  return Array.from(genresSet).sort((a, b) => a.localeCompare(b));
}
